import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams,useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Select from 'react-select';
import { useTasks } from '../../context/TaskContext';
import { useCompanyWorkForce } from '../../context/CompanyWorkForceContext';
import { useLoading } from '../../context/LoadingContext';
import { useRoutePath } from '../../utils/getRoutePath';
import Breadcrumb from '../common/Breadcrumb';
import { CalendarIcon } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Validation schema
const schema = yup.object().shape({
  type_id: yup.string().required('Task type is required'),
  start_date: yup.string().required('Start date is required'),
  end_date: yup
  .string()
  .required('End date is required')
  .test('is-after-start', 'End date cannot be before start date', function (value) {
    const { start_date } = this.parent;
    if (!start_date || !value) return true; // Skip if one is empty
    return new Date(value) >= new Date(start_date);
  }),
  end_time: yup.mixed().nullable(),
  assign_to_ids: yup.array().min(1, 'At least one assignee is required'),
  privacy: yup.boolean(),
  recurring: yup.boolean(),
  repeat_type: yup.string().when('recurring', {
    is: true,
    then: () => yup.string().required('Repeat type is required'),
    otherwise: () => yup.string().nullable(),
  }),
  repeat_every: yup.number().when('recurring', {
    is: true,
    then: () => yup.number().required('Repeat frequency is required').min(1, 'Must be at least 1'),
    otherwise: () => yup.number().nullable(),
  }),
  repeat_days: yup.array().when(['recurring', 'repeat_type'], {
    is: (recurring, repeat_type) => recurring && repeat_type === 'weekly',
    then: () => yup.array().min(1, 'Select at least one day'),
    otherwise: () => yup.array().nullable(),
  }),
  repeat_by: yup.string().when(['recurring', 'repeat_type'], {
    is: (recurring, repeat_type) => recurring && repeat_type === 'monthly',
    then: () => yup.string().required('Select how to repeat'),
    otherwise: () => yup.string().nullable(),
  }),
  end_type: yup.string().when('recurring', {
    is: true,
    then: () => yup.string().required('Select end type'),
    otherwise: () => yup.string().nullable(),
  }),
  end_after: yup.number().when(['recurring', 'end_type'], {
    is: (recurring, end_type) => recurring && end_type === 'after',
    then: () => yup.number().required('Number of occurrences is required').min(1, 'Must be at least 1'),
    otherwise: () => yup.number().nullable(),
  }),
  recurring_end_date: yup.date().when(['recurring', 'end_type'], {
    is: (recurring, end_type) => recurring && end_type === 'on',
    then: () =>
      yup
        .string()
        .required('End date is required')
        .test(
          'is-after-start',
          'Recurring end date cannot be before start date',
          function (value) {
            const { start_date } = this.parent;
            if (!start_date || !value) return true; // skip if missing
            return new Date(value) >= new Date(start_date);
          }
        ),
    otherwise: () => yup.string().nullable(),
  }),
  description: yup.string(),
});

const TaskForm = ({cData}) => {
   
  const getRoutePath = useRoutePath();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { id: routeId } = useParams();
  const queryId = searchParams.get("id");
  const id = queryId ?? routeId;
  const { addTask, updateTask, getTaskById,getTaskAddtionalData } = useTasks();
  const { getWorkForce } = useCompanyWorkForce();
  const { showLoading, hideLoading } = useLoading();
 
  const [showRecurringOptions, setShowRecurringOptions] = useState(false);
  const [selectedRepeatDays, setSelectedRepeatDays] = useState([]);
  const [assignTo, setAssignTo] = useState([]);
  const dataLoadedRef = useRef(false);
  const hasFetched = useRef(false);
  const [getEditData, setGetEditData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [taskTypes,setTaskTypes] = useState([]);
  
  const getDefaultValues = useCallback((existingData = null) => {
    const data = existingData || {};
    
    const defaults = {
      type_id: data?.type_id || '',
      start_date: data?.start_date || '',
      end_date: data?.end_date || '',
      end_time: data?.end_time ? (() => { const d = new Date(`2000-01-01T${data.end_time}`); return isNaN(d.getTime()) ? '' : d; })() : '',
      assign_to_ids: data?.assign_to_ids || [],
      privacy: data?.privacy ?? false,
      recurring: data?.recurring ?? false,
      repeat_type: data?.repeat_type || '',
      repeat_every: data?.repeat_every || 1,
      repeat_days: data?.repeat_days || [],
      repeat_by: data?.repeat_by || '',
      end_type: data?.end_type || 'never',
      end_after: data?.end_after || 1,
      recurring_end_date: data?.recurring_end_date || '',
      description: data?.description || '',
      active:data?.active ?? true,
      completed:data?.completed || false,
    };
     
    return defaults;
  }, []);

    
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    watch,
    reset,
    trigger,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: getDefaultValues(getEditData),
    mode: 'onChange'
  });
 
  useEffect(() => {
    if(cData){
      reset(getDefaultValues(cData))
      setSelectedRepeatDays(cData.repeat_days || []);
      setShowRecurringOptions(cData.recurring || false);
      setSearchParams({id:cData?.id})
    }
  }, [cData,getDefaultValues,reset,setSearchParams])
 
  // Reset form when not in edit mode
  useEffect(() => {
    if (!id) {
      reset(getDefaultValues());
    }
  }, [id, reset, getDefaultValues]);

  useEffect(() => {
    const fetchTaskAddtionalData = async () => {
      const response = await getTaskAddtionalData();
      setTaskTypes(response?.types);
       
    };
    fetchTaskAddtionalData();
  }, [getTaskAddtionalData]);

  const fetchTaskData = useCallback(async () => {
    if (id && !hasFetched.current) {
      hasFetched.current = true;
      showLoading();
      try {
        const response = await getTaskById(id);
        if (response?.status === 200 && response?.data) {
          const task = response.data;
           
            setGetEditData(task);
           // Reset form with fetched data
           reset(getDefaultValues(task));
           // Set selected repeat days for UI
           setSelectedRepeatDays(task.repeat_days || []);
            
        } else {
          toast.error('Task not found');
          navigate(getRoutePath('/tasks'));
        }
      } catch (error) {
        console.error('Error fetching task data:', error);
        toast.error('Error fetching task data');
      } finally {
        hideLoading();
      }
    }
  }, [id, getTaskById, showLoading, hideLoading, reset, getDefaultValues, navigate, getRoutePath]);

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  // Load workforce data
  useEffect(() => {
     
    const loadData = async () => {
      try {
        // Load workforce
        const response = await getWorkForce(1, { all: true });
        if (response?.data) {
          setAssignTo(response.data);
        }
 
        
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Failed to load form data');
      }
    };
    if(dataLoadedRef.current === false){
      loadData();
      dataLoadedRef.current = true;

    }
    
  }, [getWorkForce]);

  // Watch all recurring-related fields
  const recurring = watch('recurring');
  const repeatType = watch('repeat_type');
  const repeatEvery = watch('repeat_every');
  const watchedRepeatDays = watch('repeat_days');
  const repeatBy = watch('repeat_by');
  const endType = watch('end_type');
  const endAfter = watch('end_after');
  const recurringEndDate = watch('recurring_end_date');
  const startDate = watch('start_date');
 
  useEffect(() => {
    
    if (recurring && startDate) {
      setShowRecurringOptions(true);
    } else {
      setShowRecurringOptions(false);
      if (!recurring) {
        setValue('repeat_type', '');
        setValue('repeat_every', 1);
        setValue('repeat_days', []);
        setValue('repeat_by', '');
        setValue('end_type', 'never');
        setValue('end_after', 1);
        setValue('recurring_end_date', '');
        setSelectedRepeatDays([]);
      }
    }
  }, [recurring, startDate, setValue]);

   
  const handleRepeatDayChange = useCallback((day) => {
    const currentDays = watch('repeat_days') || [];
    const updatedDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day];
    setValue('repeat_days', updatedDays);
    setSelectedRepeatDays(updatedDays);
  }, [watch, setValue]);

  const handleCancel = useCallback(() => {
    reset();
    navigate(getRoutePath('/tasks'));
  }, [reset, navigate, getRoutePath]);

  const getSummaryMessage = useCallback((data) => {
    
    if (!data.recurring) {
      console.log('Recurring is false, returning null');
      return null;
    }

    let summaryMessage = 'This task repeats ';
    
    // Add frequency
    if (data.repeat_type === 'daily') {
      summaryMessage += `every ${data.repeat_every} day${data.repeat_every > 1 ? 's' : ''}`;
    } else if (data.repeat_type === 'weekly') {
      summaryMessage += `every ${data.repeat_every} week${data.repeat_every > 1 ? 's' : ''}`;
      if (data.repeat_days && data.repeat_days.length > 0) {
        const days = data?.repeat_days?.map(day => {
          switch(day) {
            case 'M': return 'Monday';
            case 'T': return 'Tuesday';
            case 'W': return 'Wednesday';
            case 'Th': return 'Thursday';
            case 'F': return 'Friday';
            case 'Sa': return 'Saturday';
            case 'Su': return 'Sunday';
            default: return day;
          }
        });
        summaryMessage += ` on ${days.join(', ')}`;
      }
    } else if (data.repeat_type === 'monthly') {
      summaryMessage += `every ${data.repeat_every} month${data.repeat_every > 1 ? 's' : ''}`;
      if (data.repeat_by === 'day') {
        summaryMessage += ' on the same day';
      } else if (data.repeat_by === 'weekday') {
        const startDate = new Date(data.start_date);
        const dayOfWeek = startDate.toLocaleDateString('en-US', { weekday: 'long' });
        const weekNumber = Math.ceil(startDate.getDate() / 7);
        const weekOrdinal = ['First', 'Second', 'Third', 'Fourth', 'Fifth'][weekNumber - 1];
        summaryMessage += ` on the ${weekOrdinal} ${dayOfWeek} of the month`;
      }
    } else if (data.repeat_type === 'yearly') {
      summaryMessage += `every ${data.repeat_every} year${data.repeat_every > 1 ? 's' : ''}`;
    }

    // Add end condition
    if (data.end_type === 'after') {
      summaryMessage += ` for ${data.end_after} occurrence${data.end_after > 1 ? 's' : ''}`;
    } else if (data.end_type === 'on' && data.recurring_end_date) {
      const endDate = new Date(data.recurring_end_date);
      summaryMessage += ` until ${endDate.toLocaleDateString('en-US', { 
        month: '2-digit', 
        day: '2-digit', 
        year: 'numeric' 
      })}`;
    } else if (data.end_type === 'never') {
      summaryMessage += ' indefinitely';
    }

    
    return summaryMessage;
  }, []);

  // Get current form values for summary
  const getCurrentSummary = useCallback(() => {
    const currentValues = {
      recurring,
      repeat_type: repeatType,
      repeat_every: repeatEvery,
      repeat_days: watchedRepeatDays,
      repeat_by: repeatBy,
      end_type: endType,
      end_after: endAfter,
      recurring_end_date: recurringEndDate,
      start_date: startDate
    };
    
    const summary = getSummaryMessage(currentValues);
    return summary;
  }, [
    recurring,
    repeatType,
    repeatEvery,
    watchedRepeatDays,
    repeatBy,
    endType,
    endAfter,
    recurringEndDate,
    startDate,
    getSummaryMessage
  ]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    showLoading();
    
    try {

      const summaryMessage = getCurrentSummary();
      
      if (summaryMessage) {
        data.recurring_note = summaryMessage
      }

      data.end_time = data.end_time instanceof Date ? data.end_time.toTimeString().slice(0, 5) : (data.end_time || '');
       
      let response;
      
      if (id) {
        // Update existing task
        response = await updateTask(id, data);
      } else {
        // Create new task
        response = await addTask(data);
      }
      
      if (response.status === 200 || response.status === 201) {
        // Show summary message
        // const summaryMessage = getCurrentSummary();
        // if (summaryMessage) {
          
        //   toast.success(summaryMessage);
        // }
        
        toast.success(id ? 'Task updated successfully!' : 'Task created successfully!');
        
        if(cData){
          navigate(getRoutePath('/calendar'));
        }else{
          navigate(getRoutePath('/tasks'));
        }
      
      } else if (response.status === 422) {
        // Handle validation errors
        if (response.errors) {
          Object.keys(response.errors).forEach(field => {
            const errorMessage = response.errors[field][0];
            setError(field, { message: errorMessage });
            toast.error(`${field}: ${errorMessage}`);
          });
        }
      } else {
        toast.error('Error saving task');
      }
    } catch (error) {
      toast.error('Error saving task');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
      hideLoading();
    }
  };
 
  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      opacity: state.data.value === '' ? 0.5 : 1,
      fontStyle: state.data.value === '' ? 'normal' : 'normal',
    }),
    singleValue: (provided, state) => ({
      ...provided,
      opacity: state.data.value === '' ? 0.5 : 1,
      fontStyle: state.data.value === '' ? 'normal' : 'normal',
    }),
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {cData ? (
      
        <></>
      ) : (
        <>
          <Breadcrumb />
          <div className="mx-auto">
            <h1 className="text-2xl font-bold text-gray-900 mb-6">
              {id ? "Edit Task" : "Create New Task"}
            </h1>
          </div>
        </>
      )}
       
      <div className="bg-white rounded-lg shadow">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-6" autoComplete="off">
          {/* Task Information */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Task Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Task Type</label>
                
                <select
                  {...register('type_id')}
                  value={watch('type_id') || ''}
                  className={`mt-1 block w-full px-3 py-2 border ${errors.type_id ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm`}
                >
                  <option value="">Select Type</option>
                  {taskTypes?.map((type) => (
                   
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
                {errors.type_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.type_id.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Assign To</label>
                <Select
                 className={` appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                  errors.assign_to_ids ? 'border-red-500' : ''
                }`}
                 isMulti
                 isSearchable
                 options={assignTo.map(option => ({
                   value: option.id,
                   label: `${option.first_name} ${option.last_name}`
                 }))}
                 value={assignTo.filter(option =>
                   (watch('assign_to_ids') || []).includes(option.id)
                 ).map(option => ({
                   value: option.id,
                   label: `${option.first_name} ${option.last_name}`
                 }))}
                 onChange={(selectedOptions) => {
                   const newValue = selectedOptions ? selectedOptions.map(option => option.value) : [];
                   setValue('assign_to_ids', newValue, {
                     shouldValidate: true,
                     shouldDirty: true,
                     shouldTouch: true
                   });
                 }}
                 styles={customStyles}
                 onBlur={() => {
                   trigger('assign_to_ids');
                 }}
                 classNamePrefix="react-select" 
                 placeholder="Select assignees..."
                   
                />
                {errors.assign_to_ids && (
                  <p className="mt-1 text-sm text-red-600">{errors.assign_to_ids.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Start Date</label>
                <div className='relative'>
                <input
                  type="text"
                  {...register('start_date')}
                  className={`datepicker mt-1 block w-full px-3 py-2 border ${errors.start_date ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm`}
                  placeholder='Select Start Date'
                  onInput={(e) => {
                    
                    setValue('start_date', e.target.value);
                  }}
               />
               <CalendarIcon className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-500' />
               </div>
                {errors.start_date && (
                  <p className="mt-1 text-sm text-red-600">{errors.start_date.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">End Date</label>
                <div className='relative'>
                <input
                  type="text"
                  {...register('end_date')}
                  className={`datepicker mt-1 block w-full px-3 py-2 border ${errors.end_date ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm`}
                  placeholder='Select End Date'
                  onInput={(e) => {
                    setValue('end_date', e.target.value);
                  }}
                />
                <CalendarIcon className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-500' />
                </div>
                {errors.end_date && (
                  <p className="mt-1 text-sm text-red-600">{errors.end_date.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">End Time</label>
                <DatePicker
                  selected={watch('end_time') instanceof Date ? watch('end_time') : null}
                  onChange={(date) => setValue('end_time', date, { shouldValidate: true })}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={30}
                  timeCaption="Time"
                  dateFormat="h:mm aa"
                  placeholderText="Select End Time"
                  wrapperClassName="w-full"
                  className={`mt-1 block w-full px-3 py-2 border ${errors.end_time ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm`}
                />
                {errors.end_time && (
                  <p className="mt-1 text-sm text-red-600">{errors.end_time.message}</p>
                )}
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                {...register('description')}
                rows="4"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
                placeholder="Enter task description..."
              />
            </div>

            <div className="flex space-x-6 mt-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('privacy')}
                  className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">Everyone can see this task</label>
              </div>
                             <div className="flex items-center">
                 <input
                   type="checkbox"
                   {...register('recurring')}
                   disabled={!startDate}
                   className={`h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded ${
                     !startDate ? 'opacity-50 cursor-not-allowed' : ''
                   }`}
                   onChange={(e) => {
                     
                     setValue('recurring', e.target.checked);
                   }}
                 />
                 <label className={`ml-2 text-sm text-gray-700 ${
                   !startDate ? 'opacity-50' : ''
                 }`}>
                   Recurring (Repeat This Task)
                   {!startDate && <span className="ml-1 text-xs text-gray-500">(Select start date first)</span>}
                 </label>
               </div>
            </div>
           
          </div>

          {/* Recurring Options */}
          {showRecurringOptions && (
            <div className="border-t border-gray-200 pt-4">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Recurring Options</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Repeats</label>
                  <select
                    {...register('repeat_type')}
                    className={`mt-1 block w-full px-3 py-2 border ${errors.repeat_type ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm`}
                  >
                    <option value="">Select Type</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                  {errors.repeat_type && (
                    <p className="mt-1 text-sm text-red-600">{errors.repeat_type.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Repeat Every
                    {repeatType === 'daily' && ' (Days)'}
                    {repeatType === 'weekly' && ' (Weeks)'}
                    {repeatType === 'monthly' && ' (Months)'}
                    {repeatType === 'yearly' && ' (Years)'}
                  </label>
                  <input
                    type="number"
                    {...register('repeat_every')}
                    min="1"
                    className={`mt-1 block w-full px-3 py-2 border ${errors.repeat_every ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm`}
                  />
                  {errors.repeat_every && (
                    <p className="mt-1 text-sm text-red-600">{errors.repeat_every.message}</p>
                  )}
                </div>
              </div>

              {/* Weekly Repeat Days */}
              {repeatType === 'weekly' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">Repeat On</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {['M', 'T', 'W', 'Th', 'F', 'Sa', 'Su'].map((day, index) => (
                       
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleRepeatDayChange(day)}
                        className={`px-3 py-1 rounded-md text-sm font-medium ${
                          selectedRepeatDays.includes(day)
                            ? 'bg-[#009efb] text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                  {errors.repeat_days && (
                    <p className="mt-1 text-sm text-red-600">{errors.repeat_days.message}</p>
                  )}
                </div>
              )}

              {/* Monthly Repeat Options */}
              {repeatType === 'monthly' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">Repeat By</label>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        {...register('repeat_by')}
                        value="day"
                        className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300"
                      />
                      <label className="ml-2 text-sm text-gray-700">Day of the month</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="radio"
                        {...register('repeat_by')}
                        value="weekday"
                        className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300"
                      />
                      <label className="ml-2 text-sm text-gray-700">Day of the week</label>
                    </div>
                  </div>
                  {errors.repeat_by && (
                    <p className="mt-1 text-sm text-red-600">{errors.repeat_by.message}</p>
                  )}
                </div>
              )}

              {/* End Options */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700">Ends</label>
                <div className="mt-2 space-y-4">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      {...register('end_type')}
                      value="never"
                      className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300"
                    />
                    <label className="ml-2 text-sm text-gray-700">Never</label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="radio"
                      {...register('end_type')}
                      value="after"
                      className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300"
                    />
                    <label className="ml-2 text-sm text-gray-700">After</label>
                    {endType === 'after' && (
                      <div className="ml-4">
                        <input
                          type="number"
                          {...register('end_after')}
                          min="1"
                          className={`block w-20 px-3 py-2 border ${errors.end_after ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm`}
                        />
                        <span className="ml-2 text-sm text-gray-500">occurrences</span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
                    {/* Radio Button + Label */}
                    <div className="flex items-center">
                      <input
                        type="radio"
                        {...register('end_type')}
                        value="on"
                        className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300"
                      />
                      <label className="ml-2 text-sm text-gray-700">On</label>
                    </div>

                    {/* Date Picker when endType is "on" */}
                    {endType === 'on' && (
                      <div className="w-full sm:w-auto">
                        <div className="relative">
                          <input
                            type="text"
                            {...register('recurring_end_date')}
                            className={`datepicker block w-full px-3 py-2 border ${
                              errors.recurring_end_date ? 'border-red-500' : 'border-gray-300'
                            } rounded-md shadow-sm focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm`}
                            placeholder="Select End Date"
                            onInput={(e) => {
                              setValue('recurring_end_date', e.target.value);
                            }}
                          />
                          <CalendarIcon className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500" />
                        </div>
                        {errors.recurring_end_date && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors.recurring_end_date.message}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                </div>
              </div>
                <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">Summary</label>
                    <div className="mt-2 space-y-4">
                         
                        {/* Live Summary */}
                        {recurring && getCurrentSummary() && (
                           
                            <div className="mt-4 bg-green-50 border border-green-400 text-green-700 px-4 py-3 rounded-md">
                             <p className="text-sm font-medium">{getCurrentSummary()}</p>
                             </div>
                          
                        )}
                    </div>
                 </div>
            </div>
          )}
 
           <div className="flex space-x-6 mt-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('active')}
                  className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">Active</label>
              </div>
            </div>
            <div className="flex space-x-6 mt-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('completed')}
                  className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">Completed</label>
              </div>
            </div>

          {/* Submit Button */}
          <div className="mt-6 flex justify-end space-x-4">

          {cData ? (
            <></>
          ) : (
            <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
          >
            Cancel
          </button>
          )}
           
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Saving...' : (id ? 'Update Task' : 'Create Task')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaskForm; 